import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import SeoHead, { SITE_URL } from '@/components/feature/SeoHead';
import { supabase } from '@/lib/supabase';
import Pagination from '@/components/base/Pagination';
import { T } from '@/hooks/useAutoTranslate';

const HERO_IMG = 'https://readdy.ai/api/search-image?query=medical%20library%20books%20knowledge%20fertility%20science%2C%20professional%20medical%20research%20environment%2C%20clean%20white%20and%20navy%20blue%20tones%2C%20books%20journals%20medical%20publications%2C%20intellectual%20academic%20atmosphere&width=1600&height=600&seq=know-hero-001&orientation=landscape';

interface DBArticle {
  id: number;
  category_id: number;
  title_zh: string;
  summary_zh: string;
  cover_img: string;
  tags: string[];
  read_time: string;
  published_at: string;
  featured: boolean;
  sort_order: number;
  article_categories: { name_zh: string } | null;
}

interface Category {
  id: number;
  name_zh: string;
  name_en: string;
  name_ru: string;
  slug: string;
  sort_order: number;
}

const catColorMap: Record<string, string> = {
  '医学知识': 'bg-[#1a3a5c]/8 text-[#1a3a5c]',
  '法律政策': 'bg-amber-50 text-amber-700',
  '干货科普': 'bg-green-50 text-green-700',
};

const catOverlayMap: Record<string, string> = {
  '医学知识': 'bg-[#1a3a5c]/85 text-white',
  '法律政策': 'bg-amber-700/85 text-white',
  '干货科普': 'bg-green-700/85 text-white',
};

function KnowledgeSeoHead() {
  const knowledgeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '行业科普知识库 - RTB吉尔吉斯斯坦试管代孕中心',
    description: '专业代孕与试管婴儿知识库，涵盖IVF技术、PGT基因筛查、代孕法律政策、费用解析等干货文章。',
    url: `${SITE_URL}/news`,
  };
  return (
    <SeoHead
      title="科普知识库｜代孕试管婴儿攻略 - RTB吉尔吉斯斯坦试管代孕中心"
      description="RTB专业知识库：IVF试管婴儿技术、PGT基因筛查、代孕法律政策、费用解析、回国手续全攻略，深度文章解答您的代孕疑问。"
      keywords="试管婴儿知识,代孕科普,IVF技术,PGT基因筛查,代孕法律"
      canonical="/news"
      jsonLd={knowledgeJsonLd}
    />
  );
}

export default function KnowledgePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeCat, setActiveCat] = useState('全部');
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [articles, setArticles] = useState<DBArticle[]>([]);
  const [loadingArticles, setLoadingArticles] = useState(true);
  const [articlesError, setArticlesError] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  const loadArticles = async () => {
    setLoadingArticles(true);
    setArticlesError('');
    try {
      const { data, error: supaErr } = await supabase
        .from('articles')
        .select('id, category_id, title_zh, summary_zh, cover_img, tags, read_time, published_at, featured, sort_order, article_categories(name_zh)')
        .eq('status', 'published')
        .lte('published_at', new Date().toISOString());
      if (supaErr) throw supaErr;
      setArticles((data ?? []) as DBArticle[]);
    } catch (err: any) {
      setArticlesError(err?.message || '加载失败，请重试');
    } finally {
      setLoadingArticles(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { data } = await supabase
        .from('article_categories')
        .select('id, name_zh, name_en, name_ru, slug, sort_order')
        .order('sort_order');
      setCategories((data ?? []) as Category[]);
    } catch {
      // 分类加载失败时静默处理，回退为空列表
    }
  };

  useEffect(() => {
    loadArticles();
    loadCategories();
  }, []);

  // 推荐文章按权重（sort_order 越小越靠前）排在最前，其余按发布时间倒序
  const sortedArticles = useMemo(() => {
    return [...articles].sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      if (a.featured && b.featured) {
        return (a.sort_order ?? 5) - (b.sort_order ?? 5);
      }
      return new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime();
    });
  }, [articles]);

  const allTags = useMemo(() => {
    const tagMap: Record<string, number> = {};
    sortedArticles.forEach((a) => {
      const tags = Array.isArray(a.tags) ? a.tags : [];
      tags.forEach((tag) => {
        tagMap[tag] = (tagMap[tag] || 0) + 1;
      });
    });
    return Object.entries(tagMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 24)
      .map(([tag, count]) => ({ tag, count }));
  }, [sortedArticles]);

  const filtered = useMemo(() => {
    let list = sortedArticles;
    if (activeCat !== '全部') {
      list = list.filter((a) => a.article_categories?.name_zh === activeCat);
    }
    if (activeTag) {
      list = list.filter((a) => {
        const tags = Array.isArray(a.tags) ? a.tags : [];
        return tags.includes(activeTag);
      });
    }
    if (search) {
      list = list.filter((a) => {
        const tags = Array.isArray(a.tags) ? a.tags : [];
        return (
          a.title_zh?.includes(search) ||
          a.summary_zh?.includes(search) ||
          tags.some((tg) => tg.includes(search))
        );
      });
    }
    return list;
  }, [sortedArticles, activeCat, activeTag, search]);

  const PAGE_SIZE = 15;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginatedArticles = useMemo(
    () => filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filtered, currentPage]
  );

  // 筛选/搜索变化时回到第一页
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCat, activeTag, search]);

  const catCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    categories.forEach((cat) => {
      counts[cat.name_zh] = articles.filter((a) => a.article_categories?.name_zh === cat.name_zh).length;
    });
    return counts;
  }, [articles, categories]);

  // 只显示有已发布文章的分类，空分类不显示
  const visibleCats = useMemo(() => {
    return categories.filter((cat) => (catCounts[cat.name_zh] || 0) > 0);
  }, [categories, catCounts]);

  const handleTagClick = (tag: string) => {
    navigate(`/tags/${tag}`);
  };

  return (
    <>
      <KnowledgeSeoHead />
      <main className="pt-16 md:pt-20">
        {/* Hero */}
        <section className="relative h-56 md:h-80 flex items-center overflow-hidden">
          <img src={HERO_IMG} alt="行业科普" className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-[#0d2137]/70"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
            <div className="text-[#c9a96e] text-sm font-medium tracking-widest uppercase mb-2">{t('knowledge_section_label')}</div>
            <h1 className="text-3xl md:text-5xl font-bold text-white">{t('knowledge_title')}</h1>
            <p className="text-white/70 text-sm mt-3">{t('knowledge_subtitle')}</p>
          </div>
        </section>

        {/* Filter & Search */}
        <section className="sticky top-16 md:top-20 z-30 bg-white border-b border-gray-100 py-4">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { setActiveCat('全部'); setActiveTag(''); }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeCat === '全部' ? 'bg-[#1a3a5c] text-white' : 'bg-[#f8f5f0] text-gray-500 hover:bg-[#f0e8d8]'
                  }`}
                >
                  {t('knowledge_cat_all')}
                </button>
                {visibleCats.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => { setActiveCat(cat.name_zh); setActiveTag(''); }}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeCat === cat.name_zh ? 'bg-[#1a3a5c] text-white' : 'bg-[#f8f5f0] text-gray-500 hover:bg-[#f0e8d8]'
                    }`}
                  >
                    {cat.name_zh}
                  </button>
                ))}
              </div>
              <div className="sm:ml-auto flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-[#f8f5f0] min-w-[200px]">
                <i className="ri-search-line text-gray-400 text-sm"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('knowledge_filter_placeholder')}
                  className="bg-transparent text-sm text-gray-600 outline-none flex-1"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main content */}
            <div className="lg:col-span-3">
              {activeTag && (
                <div className="flex items-center gap-2 mb-5 p-3 bg-[#f8f5f0] rounded-xl">
                  <span className="text-sm text-gray-500">{t('knowledge_current_tag_filter')}</span>
                  <span className="text-sm font-medium text-[#1a3a5c] bg-white px-3 py-1 rounded-full">#{activeTag}</span>
                  <button
                    onClick={() => setActiveTag('')}
                    className="ml-auto text-xs text-gray-400 hover:text-gray-600 cursor-pointer flex items-center gap-1"
                  >
                    <i className="ri-close-line"></i>{t('knowledge_clear_filter')}
                  </button>
                </div>
              )}

              {articlesError ? (
                <div className="text-center py-20">
                  <i className="ri-error-warning-line text-3xl text-red-300 mb-3 block"></i>
                  <p className="text-red-500 text-sm mb-3">{articlesError}</p>
                  <button onClick={loadArticles} className="text-[#c9a96e] text-sm cursor-pointer">{t('retry')}</button>
                </div>
              ) : loadingArticles ? (
                <div className="text-center py-20 text-gray-400">
                  <i className="ri-loader-4-line text-3xl animate-spin block mb-3"></i>
                  <p className="text-sm">{t('knowledge_loading_articles')}</p>
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-20 text-gray-400">
                  <i className="ri-article-line text-4xl mb-3 block"></i>
                  <p className="text-sm">{t('knowledge_no_articles')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {paginatedArticles.map((art) => {
                    const catName = art.article_categories?.name_zh || '';
                    const tags = Array.isArray(art.tags) ? art.tags : [];
                    return (
                      <Link
                        to={`/news/${art.id}`}
                        key={art.id}
                        className="group bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                      >
                        <div className="w-full h-48 overflow-hidden relative">
                          <img
                            src={art.cover_img || 'https://readdy.ai/api/search-image?query=medical%20fertility%20knowledge%20article%20clean&width=600&height=360&seq=know-default-001&orientation=landscape'}
                            alt={art.title_zh}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute top-3 left-3">
                            <span className={`inline-block text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm ${catOverlayMap[catName] || 'bg-[#1a3a5c]/80 text-white'}`}>{catName}</span>
                          </div>
                        </div>
                        <div className="p-5">
                          <h3 className="font-bold text-[#1a3a5c] text-sm leading-snug mb-2 line-clamp-2"><T text={art.title_zh} /></h3>
                          <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-4"><T text={art.summary_zh} /></p>
                          <div className="flex flex-wrap gap-1 mb-3">
                            {tags.slice(0, 3).map((tag) => (
                              <button
                                key={tag}
                                onClick={(e) => { e.preventDefault(); handleTagClick(tag); }}
                                className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full cursor-pointer hover:bg-[#f0e8d8] hover:text-[#c9a96e] transition-colors whitespace-nowrap"
                              >
                                #{tag}
                              </button>
                            ))}
                          </div>
                          <div className="flex items-center gap-2 text-gray-400 text-xs pt-3 border-t border-gray-100">
                            <span>{art.published_at ? art.published_at.slice(0, 10) : ''}</span>
                            <span className="ml-auto flex items-center gap-0.5 text-[#c9a96e]">
                              {t('read_more')} <i className="ri-arrow-right-line text-xs"></i>
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setCurrentPage}
              />
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1 space-y-6">
              {/* Tag Cloud */}
              <div className="bg-white rounded-2xl p-5">
                <h3 className="font-bold text-[#1a3a5c] text-sm mb-4 flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-[#c9a96e]"></i>
                  {t('knowledge_sidebar_tags')}
                </h3>
                {allTags.length === 0 ? (
                  <p className="text-xs text-gray-400">-</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {allTags.map(({ tag }) => (
                      <button
                        key={tag}
                        onClick={() => handleTagClick(tag)}
                        className="text-xs px-3 py-1.5 rounded-full cursor-pointer transition-all whitespace-nowrap bg-[#f8f5f0] text-gray-600 hover:bg-[#f0e8d8] hover:text-[#c9a96e]"
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Category stats */}
              <div className="bg-white rounded-2xl p-5">
                <h3 className="font-bold text-[#1a3a5c] text-sm mb-4 flex items-center gap-2">
                  <i className="ri-bar-chart-line text-[#c9a96e]"></i>
                  {t('knowledge_sidebar_stats')}
                </h3>
                <div className="space-y-2.5">
                  {visibleCats.map((cat) => {
                    const count = catCounts[cat.name_zh] || 0;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => { setActiveCat(cat.name_zh); setActiveTag(''); }}
                        className="w-full flex items-center justify-between text-sm cursor-pointer hover:text-[#c9a96e] transition-colors group"
                      >
                        <span className={`${activeCat === cat.name_zh ? 'text-[#c9a96e] font-medium' : 'text-gray-500'} group-hover:text-[#c9a96e]`}>{cat.name_zh}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${activeCat === cat.name_zh ? 'bg-[#c9a96e] text-white' : 'bg-[#f8f5f0] text-gray-400'}`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* CTA Card */}
              <div className="bg-[#1a2f4a] rounded-2xl p-5 text-white">
                <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-[#c9a96e] mb-3">
                  <i className="ri-customer-service-line text-sm"></i>
                </div>
                <h3 className="font-bold text-sm mb-2">{t('knowledge_sidebar_cta')}</h3>
                <p className="text-white/60 text-xs leading-relaxed mb-4">{t('knowledge_sidebar_cta_desc')}</p>
                <Link
                  to="/contact"
                  className="block text-center bg-[#c9a96e] hover:bg-[#b8944f] text-white text-xs font-medium py-2.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('knowledge_sidebar_cta_btn')}
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}